import type { FetchQuoteParams } from 'actions/InsuranceActions'
import type { QuoteVerticals } from 'api/insurance'
import { CustomisableProductIds } from 'constants/insurance'
import { isBedbankItem, isCruiseItem, isExperienceItem, isFlightItem, isLEHotelItem, isTourItem } from 'lib/checkout/checkoutUtils'
import { encode, decode } from 'lib/encoding/isomorphicBase64'
import getObjectKey from 'lib/object/getObjectKey'

interface InsuranceProductKeyOptions {
  isDomestic?: boolean
  isCruise?: boolean
  subscriptionId?: string
}

export function getInsuranceProductListKey(type: App.InsuranceProductType, options: InsuranceProductKeyOptions = {}) {
  return getObjectKey({
    type,
    isDomestic: options.isDomestic,
    isCruise: options.isCruise,
    subscriptionid: options.subscriptionId,
  })
}

export function getInsuranceQuoteKey(
  productId: string,
  options: Partial<FetchQuoteParams>,
) {
  // Normalize travellers to include only adults, children, and infants counts.
  // Exclude age details, as ages are irrelevant for insurance flows and including them can cause mismatches in unique key generation.
  const travellers = options.travellers
    ? Array.isArray(options.travellers)
      ? options.travellers.map(occupant => ({
          adults: occupant.adults,
          children: occupant.children,
          infants: occupant.infants,
        }))
      : {
          adults: options.travellers.adults,
          children: options.travellers.children,
          infants: options.travellers.infants,
        }
    : undefined

  return getObjectKey({
    ...options,
    productId,
    travellers,
    numberOfSeniors: !!options?.numberOfSeniors && options.numberOfSeniors > 0 ? options?.numberOfSeniors : undefined,
    isCruise: options.isCruise ? true : undefined,
    isFlightBooking: options.isFlightBooking ? true : undefined,
  })
}

export function isCustomisableInsuranceProduct(productId: string) {
  return CustomisableProductIds.has(productId)
}

export function insuranceTypeToInsuranceProductType(type: App.InsuranceType): App.InsuranceProductType {
  if (type === 'cfmr') return 'booking-protection'
  return 'travel-insurance'
}

const verticalChecks = [
  { check: isExperienceItem, vertical: 'experiences' },
  { check: isFlightItem, vertical: 'flights' },
  { check: (item: App.Checkout.AnyItem) => isLEHotelItem(item) || isBedbankItem(item), vertical: 'hotels' },
  { check: isTourItem, vertical: 'tours' },
  { check: isCruiseItem, vertical: 'cruises' },
] as const
export function getLuxVerticals(items: Array<App.Checkout.AnyItem>): QuoteVerticals {
  return verticalChecks
    .filter(({ check }) => items.some(check))
    .map(({ vertical }) => vertical)
}

interface ShouldFetchInsuranceQuotesProps {
  isStandalone?: boolean
  isCroBundledFlexibleFlightFaresEnabled: boolean
  bookingProtectionEnabled: boolean
  isFlightDateChange?: boolean
}

export function shouldFetchInsuranceQuotes({
  isStandalone,
  isCroBundledFlexibleFlightFaresEnabled,
  bookingProtectionEnabled,
  isFlightDateChange,
}: ShouldFetchInsuranceQuotesProps) {
  // Skip insurance quotes for flight date change scenarios
  // TODO: Check how to handle this for date change scenarios
  if (isFlightDateChange) {
    return false
  }

  if (isStandalone) {
    return true
  }

  if (isCroBundledFlexibleFlightFaresEnabled) {
    return bookingProtectionEnabled
  }

  return false
}

export function encodeCostWithInsurance(costWithInsurance?: App.FlightFareFamily['costWithInsurance']): string | undefined {
  if (!costWithInsurance) return undefined
  const toEncode = [
    costWithInsurance?.adult ?? 0,
    costWithInsurance?.children ?? 0,
    costWithInsurance?.infant ?? 0,
    costWithInsurance?.all ?? 0,
  ].join('|')

  return encode(toEncode) ?? undefined
}

export function decodeCostWithInsurance(encodedCostWithInsurance: string): App.FlightFareFamily['costWithInsurance'] | undefined {
  const decodedCostWithInsurance = decode(encodedCostWithInsurance)
  if (!decodedCostWithInsurance) return undefined
  const encodedCostWithInsuranceArray = decodedCostWithInsurance.split('|').map(Number)
  return {
    adult: encodedCostWithInsuranceArray[0],
    children: encodedCostWithInsuranceArray[1],
    infant: encodedCostWithInsuranceArray[2],
    all: encodedCostWithInsuranceArray[3],
  }
}

/**
 * Converts an array of insurance travellers to occupants format
 * @param travellers Array of insurance travellers with ageRange
 * @returns App.Occupants object
 */
export function getOccupantsFromInsuranceTravellers(travellers: Array<App.OrderInsuranceItemTraveller>): App.Occupants {
  return {
    adults: travellers.filter(traveller => (
      !traveller?.ageRange || traveller.ageRange === 'adult' || traveller.ageRange === 'senior'
    )).length,
    children: travellers.filter(traveller => traveller?.ageRange === 'child').length,
    infants: travellers.filter(traveller => traveller?.ageRange === 'infant').length,
    childrenAge: [], // Insurance travellers don't have specific ages, only age ranges
  }
}

/**
 * Resolves the traveller occupancy used for insurance quote cache keys.
 * Must stay in sync between quote fetching (useCheckoutInsuranceData) and
 * quote lookup (getCheckoutSelectedQuotes).
 */
export function getInsuranceQuoteTravellers(
  item: App.Checkout.InsuranceItem | undefined,
  defaultTravellers: App.Occupants | Array<App.Occupants> | undefined,
  isCruiseCheckout: boolean,
): App.Occupants {
  const useCustomTravellers = item?.insuranceType !== 'cfmr' && !isCruiseCheckout

  if (useCustomTravellers && item?.travellers?.length) {
    return getOccupantsFromInsuranceTravellers(item.travellers)
  }

  if (Array.isArray(defaultTravellers)) {
    return defaultTravellers[0] ?? { adults: 0, children: 0, infants: 0, childrenAge: [] }
  }

  return defaultTravellers ?? { adults: 0, children: 0, infants: 0, childrenAge: [] }
}
